import type { CarProfile } from '../../cars/CarProfile';
import { isCarBrowser } from '../../utils/platform';
import { carPath } from '../../cars/registry';
import { navigate } from '../App';
import { isAdmin, isCarUnlocked, getAccountEmail, demoLeftSeconds, demoLimitKmh, DEMO_LIMIT_KMH } from '../../utils/access';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { currentSession, scaleGearing } from '../../core/Session';
import { loadSettings, saveSettings, TOP_SPEED_OPTIONS_KMH, KMH_PER_MPH, type TelemetrySource } from '../../utils/settings';
import { ICE_CARS, ICE_BRANDS, cleanGearTops, gearTopsProblem, GEAR_LIMITS_KMH } from '../../cars/iceCars';
import { AccountModal } from '../components/AccountModal';
import { useRelayStatus, newRoomCode } from '../../utils/relayStatus';
import { defaultRelayUrl } from '../../telemetry/RemoteTelemetryProvider';
import { relayKeyForRoom } from '../../utils/relayKey';
import type { LearnState } from '../../telemetry/GearLearner';
import { usePrice } from '../../utils/price';
import { useT } from '../../i18n';
import { useOffline, ensureOffline, prefetchOwned, offlineSupported, type OfflineState } from '../../utils/offline';
import { useBuy } from '../useBuy';

/** The pre-drive "room" (sim-style pit bay): car → bay → dash, and the dash exits back into the bay.
 *  Everything about DRIVING lives here (input, hardware steps, gear ratio); the account stays in the Home drawer.
 *  Rule: the bay never blocks — DRIVE is the first thing on screen, settings sit below it. */
/** the longest word of a label: what has to fit across a round button */
export function longestWord(s: string): number {
  return Math.max(...s.split(/\s+/).map((w) => w.length), 1);
}

export function Garage({ profile, cars }: { profile: CarProfile; cars: CarProfile[] }) {
  const t = useT();
  const admin = isAdmin();
  const [email, setEmail] = useState(getAccountEmail());
  const [showAccount, setShowAccount] = useState(false);
  const [bought, setBought] = useState(false);   // the app's purchase landed: the bay stops calling it a demo
  const buy = useBuy(profile.id, () => setBought(true));
  const [sentToScreen, setSentToScreen] = useState(false);
  // the Commander row, open: its state word turns live ('connecting…' in the app, where the bridge runs)
  const [cmOpen, setCmOpen] = useState(false);
  // CAN signal hunter (admin, in-app): the app diffs every subscribed frame between two marked states
  const [huntMsg, setHuntMsg] = useState('');
  const huntMark = (which: 'a' | 'b' | 'clear') => {
    setHuntMsg(which === 'a' ? 'listening, wipers off…' : which === 'clear' ? '' : 'listening, wipers on… answer in 6 s');
    try { (window as Window & { RoarBridge?: { postMessage: (m: string) => void } }).RoarBridge?.postMessage(`hunt:${which}`); }
    catch { setHuntMsg('needs the app'); }
  };
  useEffect(() => {
    const f = () => {
      const raw = (window as Window & { __roarHunt?: ({ windows?: number } & { id: number; bit: number; off: number; on: number })[] }).__roarHunt ?? [];
      const windows = raw.find((x) => typeof x.windows === 'number')?.windows ?? 0;
      const hits = raw.filter((x) => typeof x.id === 'number');
      // one off/on pair cannot rule out a slowly drifting value: it sits still inside a window and at a
      // different level in the next. Only alternation kills that, so say how many pairs are in.
      const pairs = Math.floor(windows / 2);
      setHuntMsg(hits.length
        ? `${hits.slice(0, 8).map((x) => `0x${x.id.toString(16)} bit ${x.bit}: ${x.off}→${x.on}`).join(' · ')}${pairs < 2 ? '. Do one more off/on pair to be sure' : `. Held across ${pairs} pairs`}`
        : pairs < 2 ? 'nothing yet, do another off/on pair' : `nothing holds across ${pairs} pairs: not on this bus`);
    };
    window.addEventListener('roarhunt', f);
    return () => window.removeEventListener('roarhunt', f);
  }, []);
  // the app tells us which car the phone is plugged into (Bluetooth route name) — a Tesla lights the Tesla layer
  const [carBt, setCarBt] = useState<{ name?: string; tesla?: boolean; dbg?: string }>(() => (window as Window & { __roarCar?: { name?: string; tesla?: boolean; dbg?: string } }).__roarCar ?? {});
  useEffect(() => {
    const f = () => setCarBt({ ...(window as Window & { __roarCar?: { name?: string; tesla?: boolean; dbg?: string } }).__roarCar });
    window.addEventListener('roarcar', f);
    return () => window.removeEventListener('roarcar', f);
  }, []);
  // the tab (and the Tesla's browser chrome) showed the URL, id and all: 'garage/porsche-911-rsr-copy'.
  // It should say what the driver is looking at.
  useEffect(() => {
    const prev = document.title;
    document.title = `${profile.manufacturer} ${profile.name} · RoarDrive`;
    return () => { document.title = prev; };
  }, [profile.id]);
  // back in the bay → the engine goes quiet (the session stays alive so re-entering shows START)
  useEffect(() => {
    void currentSession()?.stopEngine();
  }, []);
  // last session's laps → the bay doubles as the debrief screen
  const session = currentSession();
  const lap = session && session.profile.id === profile.id ? session.lapTimer.getState() : null;
  const fmtLap = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, '0')}`;
  const unlocked = profile.available && (bought || isCarUnlocked(profile.id) || profile.free);
  // THE ENGINE ON THIS DEVICE (utils/offline.ts). Every drive starts here and every dash exits here, so the
  // bay is where the download is asked for and where its state is read; the other owned cars follow.
  const off = useOffline(profile.id);
  useEffect(() => { if (unlocked) void ensureOffline(profile).then(() => prefetchOwned()); }, [unlocked, profile.id]);
  const mb = (b: number) => (b >= 10e6 ? String(Math.round(b / 1e6)) : (b / 1e6).toFixed(1));
  const offlineVerdict = (o: OfflineState): string =>
    o.state === 'ready' ? t('offlineReady') : o.state === 'downloading' ? t('offlineDownloading') : o.state === 'failed' ? t('offlineFailed') : o.state === 'waiting' ? t('offlineNoNet') : t('offlineChecking');
  const offlineMeta = (o: OfflineState): string =>
    o.state === 'ready' ? t('offlineReadyMeta', { mb: mb(o.bytes) }) : o.state === 'downloading' ? t('offlineProgress', { done: mb(o.doneBytes), total: mb(o.totalBytes) }) : o.state === 'failed' ? t('offlineFailedMeta') : o.state === 'waiting' ? t('offlineNoNetMeta') : '';
  // Pro hardware ships WITH the engine (Marc): demo drivers get GPS only
  const proOk = true;   // ANY source in demo (Marc): the 2 minutes and the pit limiter live in the dash, not here
  const demoLeft = demoLeftSeconds(profile.id);
  const price = usePrice();

  // ---- gear ratio (moved from the Home drawer) ----
  const [vehicleTop, setVehicleTop] = useState(() => loadSettings().vehicleTopSpeedKmh);
  const [units, setUnits] = useState<'kmh' | 'mph'>(() => loadSettings().units);
  const pickTop = (kmh: number) => {
    setVehicleTop(kmh);
    saveSettings({ vehicleModel: 'custom', vehicleTopSpeedKmh: kmh });
    currentSession()?.setTopSpeed(kmh);
  };
  const pickUnits = (u: 'kmh' | 'mph') => { setUnits(u); saveSettings({ units: u }); };
  const [kind, setKind] = useState<'ev' | 'ice'>(() => loadSettings().vehicleKind);
  const [gearing, setGearing] = useState<'porsche' | 'own'>(() => loadSettings().gearing);
  const pickGearing = (g: 'porsche' | 'own') => { setGearing(g); saveSettings({ gearing: g }); currentSession()?.setGearing(g); };
  const [iceCar, setIceCar] = useState<string>(() => loadSettings().vehicleCar ?? '');
  const [iceBrand, setIceBrand] = useState<string>(() => { const id = loadSettings().vehicleCar; return id === 'custom' ? 'custom' : ICE_CARS.find((c) => c.id === id)?.brand ?? ''; });
  const [customGears, setCustomGears] = useState<string[]>(() => (loadSettings().vehicleGearTopsKmh ?? [45, 80, 120, 160, 195, 200]).map(String));
  const applyGears = (tops: number[] | null, carId: string | null) => {
    saveSettings({ vehicleKind: 'ice', vehicleGearTopsKmh: tops, vehicleCar: carId });
    currentSession()?.setGearTops(tops);
  };
  const pickKind = (k: 'ev' | 'ice') => {
    setKind(k);
    if (k === 'ev') { saveSettings({ vehicleKind: 'ev' }); currentSession()?.setGearTops(null); }
    else { const car = ICE_CARS.find((c) => c.id === iceCar); applyGears(car ? car.gears : cleanGearTops(customGears), car ? car.id : (iceCar === 'custom' ? 'custom' : null)); }
  };
  const pickIceCar = (id: string) => {
    setIceCar(id);
    if (id === 'custom') applyGears(cleanGearTops(customGears), 'custom');
    else { const car = ICE_CARS.find((c) => c.id === id); if (car) { setIceBrand(car.brand); applyGears(car.gears, car.id); } }
  };
  // the ICE ladder as shown: the picked car's, or the driver's own once any cell has been touched
  const [edited, setEdited] = useState(false);
  const baseCar = ICE_CARS.find((c) => c.id === iceCar) ?? null;
  const isCustom = iceCar === 'custom' || iceBrand === 'custom' || edited;
  const gearsOf = (id: string): number[] | null => (id === 'custom' ? cleanGearTops(customGears.map((x) => (units === 'mph' ? Number(x) * KMH_PER_MPH : Number(x)))) : ICE_CARS.find((c) => c.id === id)?.gears ?? null);
  const gearSpeed = (kmh: number) => (units === 'mph' ? Math.round(kmh / KMH_PER_MPH) : kmh);
  const shownGears: number[] | null = isCustom ? cleanGearTops(customGears.map((x) => (units === 'mph' ? Number(x) * KMH_PER_MPH : Number(x)))) ?? customGears.map((x) => (units === 'mph' ? Number(x) * KMH_PER_MPH : Number(x))) : (iceCar ? gearsOf(iceCar) : null);
  const evLadder = scaleGearing(profile, Math.min(vehicleTop, profile.gearTopSpeedsKmh[profile.gearTopSpeedsKmh.length - 1]));
  // editing a cell of a listed car seeds 'your gears' from it first, so the other cells keep their values
  const editCell = (i: number, v: string) => {
    if (!isCustom && shownGears) { setCustomGears(shownGears.map((x) => String(gearSpeed(x)))); setEdited(true); }
    const base = !isCustom && shownGears ? shownGears.map((x) => String(gearSpeed(x))) : customGears;
    const next = base.slice(); next[i] = v; setCustomGears(next);
    const clean = cleanGearTops(next.map((x) => (units === 'mph' ? Number(x) * KMH_PER_MPH : Number(x))));
    if (clean) applyGears(clean, 'custom');
  };
  const ladderTable = (tops: number[], editable: boolean) => {
    const max = tops[tops.length - 1] || 1;
    const raw = isCustom ? customGears : tops.map((x) => String(gearSpeed(x)));
    return (
      <ol class="ladder" style={{ '--n': tops.length } as Record<string, number>}>
        {tops.map((v, i) => (
          <li key={i} style={{ '--w': Math.min(1, v / max) } as Record<string, number>}>
            <em>{i + 1}</em>
            {editable ? <input inputMode="numeric" value={isCustom ? raw[i] ?? '' : String(gearSpeed(v))} onInput={(e) => editCell(i, (e.currentTarget as HTMLInputElement).value)} /> : <b>{gearSpeed(v)}</b>}
            <i />
          </li>
        ))}
      </ol>
    );
  };
  const showSpeed = (kmh: number) => (units === 'mph' ? `${Math.round(kmh / KMH_PER_MPH)} mph` : `${kmh} km/h`);
  const carTop = Math.max(...cars.filter((c) => !c.adminOnly).map((c) => c.realTopSpeedKmh ?? 0), TOP_SPEED_OPTIONS_KMH[TOP_SPEED_OPTIONS_KMH.length - 1]);
  const carTopName = cars.find((c) => !c.adminOnly && c.realTopSpeedKmh === carTop)?.name ?? null;
  const topList = [...TOP_SPEED_OPTIONS_KMH.filter((k) => k < carTop), carTop];
  const topClamped = topList.reduce((best, k) => (Math.abs(k - vehicleTop) < Math.abs(best - vehicleTop) ? k : best), topList[0]);
  const topOptions = topList;
  const topLabel = (kmh: number) => (kmh === carTop && carTopName ? t('carLimit', { speed: showSpeed(kmh), name: carTopName }) : showSpeed(kmh));

  // ---- driving input (moved from the Home drawer) ----
  const [source, setSource] = useState<TelemetrySource>(() => {
    const s = loadSettings().source;
    return s;
  });
  const pickSource = (src: TelemetrySource) => {
    setSource(src);
    saveSettings({ source: src });
    currentSession()?.setTelemetrySource(src);
  };
  // EXACTLY ONE card is lit at a time (Marc could light the Tesla box and the Pro box together). Phone and
  // Tesla are the same telemetry source — the browser's geolocation — so the choice between them is a UI
  // one, remembered here; only Pro switches the engine to the relay.
  // Sources STACK (Marc: 'que se pueda seleccionar más de 1 a la vez, a lo fallback'). The engine already
  // worked this way — the bridge fuses RaceBox over OBD over the Commander, and GPS covers the gaps — it
  // was only this screen that pretended they were exclusive. Phone GPS is not a choice at all: it is the
  // floor everything falls back to, so it is shown as such instead of as a rival.
  type Src = 'tesla' | 'pro' | 'sim';
  const [picks, setPicks] = useState<Src[]>(() => {
    if (source === 'sim') return ['sim'];
    try {
      const raw = localStorage.getItem('roardrive:sources');
      if (raw) return JSON.parse(raw) as Src[];
    } catch { /* private mode or older value */ }
    return source === 'can' ? ['pro'] : [];
  });
  const on = (p: Src) => picks.includes(p);
  // SIM or SIM ADMIN: the same telemetry source, one flag apart (the flag opens the instrumented screen)
  const [simAdmin, setSimAdmin] = useState(() => loadSettings().simAdmin);
  const pickSim = (asAdmin: boolean) => {
    saveSettings({ simAdmin: asAdmin });
    setSimAdmin(asAdmin);
    if (!on('sim')) toggle('sim');
    else if (simAdmin === asAdmin) toggle('sim');   // tapping the one that is already on switches the SIM off
  };
  const toggle = (p: Src) => {
    const next = on(p) ? picks.filter((x) => x !== p) : [...picks.filter((x) => (p === 'sim' ? false : x !== 'sim')), p];
    setPicks(next);
    try { localStorage.setItem('roardrive:sources', JSON.stringify(next)); } catch { /* private mode */ }
    pickSource(next.includes('sim') ? 'sim' : next.includes('pro') ? 'can' : 'gps');
  };
  const [room] = useState(() => {
    const r = loadSettings().relayRoom;
    if (!r || r === 'roar') { const n = newRoomCode(); saveSettings({ relayRoom: n }); return n; }
    return r;
  });
  const inApp = /ROARDRIVE-App/.test(navigator.userAgent);
  // SELLING FROM THE WEB TODAY (Marc): the dongles and the phone's own GPS both arrive through the phone
  // app, and the app is still with Apple. So on the web those two are ANNOUNCED, not offered: what the
  // web sells and delivers right now is the car's own browser, and the simulator for anyone else.
  // In the app nothing changes: they work there today.
  const soonOnWeb = !inApp;
  // the SIM left the web: a browser that still had it picked falls back to the road sources
  useEffect(() => {
    if (inApp || admin || !picks.includes('sim')) return;
    const next = picks.filter((x) => x !== 'sim');
    setPicks(next);
    try { localStorage.setItem('roardrive:sources', JSON.stringify(next)); } catch { /* private mode */ }
    pickSource(next.includes('pro') ? 'can' : 'gps');
  }, [inApp, admin, picks]);
  // Which GPS this page has is MEASURED, not guessed. Sniffing the user agent for 'Tesla' got it wrong
  // for Marc sitting in his own car — the bay told him he was on phone GPS at 1 Hz while he was reading
  // it from the Tesla's screen. So the page times its own fixes and believes the number.
  // the same test the rest of the app uses: a Model S/X from before 2018 answers QtCarBrowser, not Tesla,
  // and it was falling through to the phone's story on its own screen
  const uaCar = !inApp && isCarBrowser();
  const [gps, setGps] = useState<{ hz: number; peak: number; fixes: number; speed: boolean; state: string }>(
    { hz: 0, peak: 0, fixes: 0, speed: false, state: 'detecting' });
  // a car's browser feeds several fixes a second; a phone manages about one. The PEAK is what counts:
  // a Tesla throttles to ~0.3 Hz standing still and opens to 10 Hz the moment it rolls, so the highest
  // rate seen this session is the honest description of what this device can do.
  const inCarScreen = uaCar || gps.peak >= 4;
  useEffect(() => {
    if (!('geolocation' in navigator)) { setGps((g) => ({ ...g, state: 'unavailable' })); return; }
    let watch: number | null = null;
    let arrivals: number[] = [];
    const start = () => {
      watch = navigator.geolocation.watchPosition((p) => {
        const now = performance.now();
        arrivals.push(now);
        if (arrivals.length > 12) arrivals.shift();
        const hz = arrivals.length >= 2 ? 1000 * (arrivals.length - 1) / (arrivals[arrivals.length - 1] - arrivals[0]) : 0;
        setGps((g) => ({
          hz,
          peak: Math.max(g.peak, hz),
          fixes: g.fixes + 1,
          speed: g.speed || (p.coords.speed != null && Number.isFinite(p.coords.speed)),
          state: 'live',
        }));
      }, () => setGps((g) => ({ ...g, state: 'denied' })), { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 });
    };
    // never prompt from this screen uninvited: measure only where the answer is already yes, or where the
    // driver is plainly about to drive on it anyway (a car's own browser)
    const perms = (navigator as Navigator & { permissions?: { query: (d: { name: string }) => Promise<{ state: string }> } }).permissions;
    if (uaCar) start();
    else if (perms) perms.query({ name: 'geolocation' }).then((st) => { if (st.state === 'granted') start(); else setGps((g) => ({ ...g, state: 'ask' })); }).catch(() => setGps((g) => ({ ...g, state: 'ask' })));
    else setGps((g) => ({ ...g, state: 'ask' }));
    return () => { if (watch != null) navigator.geolocation.clearWatch(watch); };
  }, [uaCar]);
  // Show the LIVE rate: pinning the display to the session peak made it look frozen (Marc watched it sit
  // at 12.2 Hz). The peak still decides WHAT this device is — that judgement should not flip at a red
  // light — but the number on screen has to keep moving, because a number that never moves reads as dead.
  const gpsMeasured = gps.state === 'live' && gps.fixes >= 2;
  // rounded to the half hertz: the raw figure jitters with every fix, and a number that never settles
  // reads as broken rather than as live (which is what the frozen peak looked like from the other side)
  const gpsLabel = gpsMeasured ? `${(Math.round(gps.hz * 2) / 2).toFixed(1)} Hz` : 'detecting…';
  // admin: MEASURED geolocation rate (Marc: 'cuántos hercios tiene el GPS del navegador de Tesla?') —
  // nobody publishes it, so the bay counts real watchPosition fixes and shows Hz + whether speed comes
  // NEVER silent (the first cut sat at 'measuring…' forever in the Tesla: no timeout, so a missing
  // permission or a dry watch fired NO callback at all). Now every state is reported: permission,
  // seconds waited, fix count, arrival rate, and whether coords.speed comes through.
  const [gpsHz, setGpsHz] = useState('starting…');
  // shown for admins, or for any session that opened the site with ?gps=1 — the Tesla browser carries no
  // admin session, so that flag is how the rate gets read from the car seat. It STICKS for the session,
  // because tapping through to the bay drops the query string.
  // NOT on by default any more, admin included (Marc: 'quítame lo del gps rate debajo del DRIVE, ya lo
  // hemos comprobado') — it survives only as the instrument for measuring the NEXT car's browser: open
  // the site with ?gps=1 and the reading appears at the foot of this section.
  const showGps = useMemo(() => {
    try {
      if (location.search.includes('gps')) sessionStorage.setItem('roarGpsMeter', '1');
      return sessionStorage.getItem('roarGpsMeter') === '1';
    } catch { return false; }
  }, []);
  useEffect(() => {
    // NOT for everyone: an unasked-for permission prompt the moment you walk into the bay is exactly the
    // kind of thing a user should never be hit with — the meter only watches when it is being read
    if (!showGps) return;
    if (!('geolocation' in navigator)) { setGpsHz('no geolocation API'); return; }
    const arrivals: number[] = [];   // performance.now() per callback — the rate the PAGE actually sees
    let fixes = 0, spd = '—', perm = '?';
    const t0 = performance.now();
    try {
      (navigator as Navigator & { permissions?: { query: (d: { name: string }) => Promise<{ state: string }> } })
        .permissions?.query({ name: 'geolocation' }).then((s) => { perm = s.state; }).catch(() => { /* not supported */ });
    } catch { /* not supported */ }
    // a heartbeat so the line always moves: waiting states show the seconds and the permission
    const tick = setInterval(() => {
      if (fixes === 0) setGpsHz(`waiting ${((performance.now() - t0) / 1000).toFixed(0)}s · perm ${perm}`);
    }, 1000);
    const id = navigator.geolocation.watchPosition((p) => {
      fixes++;
      arrivals.push(performance.now());
      if (arrivals.length > 15) arrivals.shift();
      if (p.coords.speed != null && Number.isFinite(p.coords.speed)) spd = '✓';
      if (arrivals.length >= 2) {
        const dt = (arrivals[arrivals.length - 1] - arrivals[0]) / (arrivals.length - 1);
        setGpsHz(`${(1000 / dt).toFixed(1)} Hz · ${fixes} fixes · speed ${spd}`);
      } else {
        setGpsHz(`1st fix · speed ${spd}`);
      }
    }, (e) => setGpsHz(`err ${e.code}: ${e.message} · perm ${perm}`), { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 });
    return () => { clearInterval(tick); navigator.geolocation.clearWatch(id); };
  }, [showGps]);
  // ALWAYS listen. Gating this on 'can' meant the car screen only learned about the phone's bridge while
  // Pro hardware was selected: the Commander went green and then dropped as soon as the source settled on
  // GPS, and pressing Pro appeared to 'fix' a card it has nothing to do with (Marc found both). The bay is
  // a pre-drive screen; one status socket is the right price for the dots telling the truth.
  const relay = useRelayStatus(room, true);
  // what is ACTUALLY driving the engine right now, best first — the answer to 'so which one is it using?'
  // …and it wears the colour of whatever is driving: green for hardware, blue for the car's own browser,
  // amber for the phone — the same ladder the cards use, so the line and the cards agree at a glance
  // EVERYTHING that is alive, not just the winner (Marc: 'debería salir todos los sources'). The speed
  // comes from the first of these; the Commander rides alongside it bringing gear, pedals and launch,
  // which is exactly the combination worth showing off. Always a number: inside the app the page has no
  // watch of its own, so 'measured' is usually false there and the figure would vanish.
  // one per line, each in its own tier's colour: a joined string made three very different things look
  // like one, and hid which of them was carrying the speed (Marc)
  const simOn = on('sim');

  // ---- the gearbox the app MEASURES, instead of the one it guesses from a catalogue ----
  // Speed and rpm both come from the OBD dongle, and their quotient is the gear you are in. The card
  // below shows what has been learned so far and offers it when it is sure. In the SIM it watches the
  // simulator's own gearbox instead, which is how this got tested without a dongle in a car.
  const [learn, setLearn] = useState<LearnState | null>(null);
  const [following, setFollowing] = useState(0);
  const [followOn, setFollowOn] = useState(() => loadSettings().followMyGears);
  const learnSim = on('sim');
  useEffect(() => {
    const read = () => { setLearn(currentSession()?.gearLearning(learnSim) ?? null); setFollowing(currentSession()?.followingGear() ?? 0); };
    read();
    const id = setInterval(read, 1500);
    return () => clearInterval(id);
  }, [learnSim]);
  const learnedLadder = () => currentSession()?.gearLearningLadder(learnSim) ?? [];

  const drivingList = simOn ? [{ what: t('sim'), tone: 'sim' }] : [
    relay.raceboxOk ? { what: 'RaceBox 25 Hz', tone: 'best' } : null,
    relay.obdOk ? { what: 'OBD-II', tone: 'best' } : null,
    inCarScreen ? { what: `Tesla browser ${gpsMeasured ? gpsLabel : '10 Hz'}`, tone: 'good' } : null,
    relay.canOk ? { what: 'Enhauto Commander', tone: 'good' } : null,
    // the phone's own GPS is the engine's floor in the app; on the web it is announced, not offered,
    // so the bay must not name it as the live source (Marc: one story, not two)
    !inCarScreen && inApp ? { what: `Phone GPS ${gpsMeasured ? gpsLabel : '1 Hz'}`, tone: 'mid' } : null,
  ].filter(Boolean) as { what: string; tone: string }[];
  // The headless bridge follows the BAY, not the driving session (which does not exist yet here — that
  // gap is what kept the status dots grey). It powers up for Pro hardware AND for the Tesla card, because
  // the Commander lives in the Tesla card now: telling someone there to "pick Pro hardware so the bridge
  // runs" contradicted the whole point of putting it there (Marc).
  // depend on the MESSAGE, not on a boolean: 'pro' and 'tesla' both made bridgeWanted true, so switching
  // between them never re-fired this and the bridge stayed in whichever mode it started in
  const bridgeCmd = on('pro') ? 'on' : on('tesla') ? 'on:cm' : 'off';
  useEffect(() => {
    if (!inApp) return;
    try { (window as Window & { RoarBridge?: { postMessage: (m: string) => void } }).RoarBridge?.postMessage(bridgeCmd); } catch { /* older app */ }
  }, [bridgeCmd, inApp]);
  // in-app the page itself keeps a LIGHT publisher socket: it hears live sub counts (a Tesla page joining
  // the room shows up in seconds) and carries the play-on-screen control — works on GPS and Pro alike
  const [subCount, setSubCount] = useState(0);
  const playWs = useRef<WebSocket | null>(null);
  useEffect(() => {
    if (!inApp) return;
    let closed = false;
    let ws: WebSocket | null = null;
    let key = '';
    const open = () => {
      if (closed) return;
      if (!key) {
        void relayKeyForRoom(room).then((k) => { key = k; if (!closed) open(); }).catch(() => { if (!closed) setTimeout(open, 4000); });
        return;
      }
      try { ws = new WebSocket(`${defaultRelayUrl(room)}?role=pub&key=${encodeURIComponent(key)}`); } catch { return; }
      playWs.current = ws;
      ws.onmessage = (ev) => { try { const m = JSON.parse(String(ev.data)); if (m.type === 'relay') setSubCount(m.subs ?? 0); } catch { /* ignore */ } };
      ws.onclose = () => { playWs.current = null; if (!closed) setTimeout(open, 3000); };
    };
    open();
    return () => { closed = true; try { ws?.close(); } catch { /* ignore */ } };
  }, [inApp, room]);
  // this page's OWN status sub counts as 1 in-app → an external screen means subs >= 2
  const screensLive = Math.max(subCount, relay.screens);

  // buying from the app goes through Apple; anywhere else the link walks to the web checkout as before
  const unlockLink = buy.canBuy
    ? (buy.onSale === false
      ? <button class="bay__unlock" disabled>{t('notInAppYet')}</button>
      : <button class="bay__unlock" disabled={buy.busy} onClick={() => void buy.buy()}>{buy.busy ? buy.busyLabel : t('unlockFor', { price: buy.price || price })}</button>)
    : <a href={`/unlock/${carPath(profile)}`}>{t('unlockFor', { price })}</a>;

  return (
    <main class={`bay ${inApp ? 'bay--app' : ''}`} style={{ '--car-accent': profile.hud?.brand ?? profile.hud?.accent ?? 'var(--accent)' } as Record<string, string>}>
      <header class="topbar bay__top">
        {/* The way out, not the brand. The logo used to sit here and double as the link home: it read as a
            masthead, so the one action this corner offers was invisible. A car page is somewhere you go
            INTO, and the only thing the top-left has to say is how to come back (Marc). */}
        <a class="bay__back" href="/" aria-label="MAIN">
          <svg class="bay__backarrow" viewBox="0 0 16 16" aria-hidden="true"><path d="M10 3 5 8l5 5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="square" /></svg>
          <span>MAIN</span>
        </a>
        <div class="bay__car">
          {/* this screen has a name of its own (Marc: 'SETUP ROOM? BOX?'): the pit box, then the car in it */}
          <span class="bay__maker">{profile.manufacturer} {profile.name}</span>
          <span class="bay__name">{t('pitBox')}</span>
        </div>
      </header>

      <div class="bay__cols">
      <section class="bay__hero">
        {profile.photo && <img class="bay__photo" src={profile.photo.src} alt="" />}
        {/* the word inside the circle is a different length in every language ('CONDUCIR' broke out of it):
            the type is sized from the longest word so it always fits, whatever we call it */}
        <button class="startbtn bay__drive" style={{ '--len': longestWord(t('drive')) } as Record<string, number>} onClick={() => navigate(`/car/${carPath(profile)}`)}>{t('drive')}</button>
        {/* two words under the button and nothing else (Marc): DRIVE, safe. The paragraph that was here
            read like a warning label; the clauses behind it live in the terms, where clauses belong. */}
        <span class="bay__drivesafe">{t('driveSafe')}</span>
        {/* Tesla detected → say so under DRIVE, then either the hand-off button (screen linked) or the
            recommendation (Marc: 'Tesla detected + We recommend driving on your car's browser') */}
        {inApp && carBt.tesla && (
          <div class="bay__sub bay__sub--tesla"><b>{t('teslaDetected', { name: carBt.name ?? '' })}</b></div>
        )}
        {inApp && screensLive < 2 && carBt.tesla && (
          <div class="bay__sub">{t('recommendCarBrowser', { site: 'roardrive.com', play: t('playThere') })}</div>
        )}
        {inApp && source === 'can' && screensLive < 2 && !carBt.tesla && (
          <div class="bay__sub">{t('carScreenNotLinked', { site: 'roardrive.com', play: t('playThere') })}</div>
        )}
        {!unlocked && (
          <div class="bay__sub">
            {demoLeft > 0
              ? <>{demoLimitKmh() == null ? t(inApp ? 'demoLineOpen' : 'demoLineOpenWeb', { min: Math.ceil(demoLeft / 60) }) : t(inApp ? 'demoLine' : 'demoLineWeb', { min: Math.ceil(demoLeft / 60), cap: DEMO_LIMIT_KMH })}{unlockLink}</>
              : <>{t(inApp ? 'demoUsed' : 'demoUsedWeb')}{unlockLink}</>}
          </div>
        )}
        {lap && lap.bestS != null && (
          <div class="bay__lap">{t('lastSession')}{lap.circuitName ? ` · ${lap.circuitName}` : ''} · {t('best')} <b>{fmtLap(lap.bestS)}</b>{lap.lapCount > 0 ? ` · ${lap.lapCount === 1 ? t('lap1') : t('laps', { n: lap.lapCount })}` : ''}</div>
        )}
      </section>

      <div class="bay__panels">
      <div class="bay__section">
        <div class="eyebrow bay__eyebrow">{t('drivingSource')}</div>
        {/* plain-words explanation for someone who has never heard of a refresh rate (Marc: 'yo soy un
            tío que no tengo ni idea, 25 Hz qué es esto') — it says WHY the number matters, not what it is */}
        <p class="bay__why">{t('drivingSourceWhy')}</p>
        {drivingList.length > 0 && (
          <div class="bay__now">{t('drivingOn')}
            <ul>{drivingList.map((d) => <li key={d.what} class={`bay__now--${d.tone}`}>{d.what}</li>)}</ul>
          </div>
        )}
        <div class="setup">
          <section class="setup__block">
            <div class="inputs">
              {/* What a driver actually feels is DELAY, not hertz (Marc: 'no me pongas el box de 1 Hz, 10 Hz,
                  queda feo') — so each source states its lag, in its own colour. And the middle option is
                  named TESLA BROWSER, never 'car screen': it has to be clear it means the Tesla's own
                  browser and not any dashboard ('cualquiera se va a pensar que su car screen vale'). */}
              {/* ONE ladder, three rungs, and EXACTLY ONE of them selected at a time (Marc: he could light
                  the Tesla box and the Pro box at once). The verdict word carries the palette — amber for
                  a moderate delay, green for great, violet for instant — while the brand red is reserved
                  for the selected outer box and nothing else. */}
              {/* the Pro card EXPANDS in place (Marc). Note it goes green for RaceBox and OBD-II only:
                  the Commander belongs to the Tesla card and lighting this one up for it was miscategorising
                  the very thing we had just finished separating. */}
              {/* the Pro card EXPANDS in place (Marc): everything hardware lives INSIDE this box — identical app/web */}
              <div class={`inp inp--src inp--host inp--pro ${on('pro') && !soonOnWeb ? 'inp--on' : ''} ${simOn ? 'inp--muted' : ''} ${on('pro') && !soonOnWeb && (relay.raceboxOk || relay.obdOk) ? 'inp--detected inp--detected--best' : ''} ${!proOk || soonOnWeb ? 'inp--soon' : ''} ${soonOnWeb ? 'inp--comingsoon' : ''}`} role="button" tabIndex={0} aria-disabled={!proOk || soonOnWeb} onClick={() => { if (proOk && !soonOnWeb) toggle('pro'); }}>
                <span class="inp__title">{t('proHw')}<em class="inp__grade inp__grade--best">{t('instant')}</em>{!soonOnWeb && !proOk && <em class="inp__soon">{t('fullEngine')}</em>}</span>
                <span class="inp__meta">{t('proHwMeta')}</span>
                <span class="inp__desc"><span>{!proOk ? t('proHwDescDemo') : t('proHwDesc')}</span></span>
                {soonOnWeb && <em class="inp__soon inp__soon--foot">{t('comingSoon')}</em>}
                {on('pro') && !soonOnWeb && (
                  <div class="hwpanel" onClick={(e) => e.stopPropagation()}>
                    {/* minimal: NO inner box — a hairline, then one collapsed row per device. Tap the one
                        you own to see its one-liner; a green dot + live status appear when it flows. */}
                    {!inApp && (
                      <details class="hwrow">
                        <summary><i class={relay.pubs > 0 ? 'hwrow__ok' : ''} /><b>{t('appRow')}</b><em>{relay.pubs > 0 ? t('linked') : ''}</em></summary>
                        <small>{t('appRowBody', { who: email ? t('appRowWithEmail', { email }) : t('appRowNoEmail'), pro: t('proHw') })}{!email ? <> {t('appRowSignInHere', { link: '' })}<button class="hwrow__link" onClick={() => setShowAccount(true)}>{t('appRowSignInLink')}</button></> : null}</small>
                      </details>
                    )}
                    {!carBt.tesla && <details class="hwrow">
                      <summary><i class={relay.obdOk ? 'hwrow__ok' : relay.obdStatus === 'connected' ? 'hwrow__mid' : ''} /><b>{t('obdRow')}</b><em class={relay.obdOk || relay.obdStatus === 'connected' ? '' : 'hwrow__hint'}>{relay.obdOk ? t('live') : relay.obdStatus === 'connected' ? t('connectedIgnition') : ''}</em></summary>
                      <ol class="hwsteps">
                        <li>{t('obdStep1')}</li>
                        <li>{t('obdStep2')}</li>
                        <li class={relay.obdOk ? 'hwsteps__done' : ''}>{t('obdStep3')}</li>
                      </ol>
                      {/* 'que no se compren uno que no sirve' (Marc): the dongle is the only thing here the
                          driver has to BUY blind, and half the cheap ones are classic Bluetooth 3.0, which
                          the iPhone cannot even see. The requirements fold away under an i, so the three
                          steps stay three steps. */}
                      <details class="hwrow hwnote">
                        <summary><span class="hwnote__i">i</span><b>{t('obdBuyTitle')}</b></summary>
                        <ul class="hwbuy">
                          <li>{t('obdBuyBle')}</li>
                          <li>{t('obdBuyElm')}</li>
                          <li>{t('obdBuyWifi')}</li>
                          <li>{t('obdBuyPrice')}</li>
                        </ul>
                        <p class="hwrow__lead">{t('obdBuyNames')}</p>
                        <p class="hwrow__lead hwbuy__ev">{t('obdBuyEv')}</p>
                      </details>
                    </details>}
                    <details class="hwrow">
                      <summary><i class={relay.raceboxOk ? 'hwrow__ok' : relay.rbStatus === 'connecting' || relay.rbStatus === 'connected' ? 'hwrow__mid' : ''} /><b>{t('raceboxRow')}</b><em>{relay.raceboxOk ? t('live') : relay.rbStatus === 'connected' ? t('connectedSatellites') : relay.rbStatus === 'connecting' ? t('foundConnecting') : relay.rbStatus === 'scanning' ? t('searching') : ''}</em></summary>
                      <ol class="hwsteps">
                        <li>{t('rbStep1')}</li>
                        <li>{t('rbStep2')}</li>
                        <li class={relay.raceboxOk ? 'hwsteps__done' : ''}>{t('rbStep3', { state: relay.rbStatus === 'scanning' ? t('stateSearching') : relay.rbStatus === 'connecting' ? t('stateFound') : relay.rbStatus === 'connected' && !relay.raceboxOk ? t('stateSatellites') : '' })}</li>
                      </ol>
                    </details>
                    <p class="hwpanel__foot">{relay.hz > 0 ? t('fuseLive', { hz: relay.hz }) : inApp ? t('startingBridge') : t('fuseFoot')}</p>
                    {inApp && !('RoarBridge' in window) && <a class="pill pill--btn" href="#can-bridge">{t('openBridge')}</a>}
                  </div>
                )}
              </div>
              {/* ALWAYS here, including when the page is running on the Tesla itself: hiding it there took
                  the Commander with it, and the Commander is the whole reason it lives in this card. When
                  we are on that screen it marks itself and shows the rate it is actually delivering. */}
              {(
                <div class={`inp inp--src inp--host inp--info ${on('tesla') || inCarScreen ? 'inp--on' : ''} ${simOn ? 'inp--muted' : ''} ${inCarScreen ? 'inp--detected inp--detected--good inp--fixed' : (inApp && (screensLive >= 2 || carBt.tesla)) ? 'inp--avail--good' : ''} inp--open`} role="button" tabIndex={0} onClick={() => { if (!inCarScreen) toggle('tesla'); }}>
                  <span class="inp__title">{t('teslaBrowser')}<em class="inp__grade inp__grade--good">{t('fast')}</em></span>
                  {/* fixed text: this line describes the SOURCE, and a number that moves with every fix
                      made the card look like a readout. The live figure belongs to the driving-on line. */}
                  <span class="inp__meta">{t('teslaMeta')}</span>
                  <span class="inp__desc"><span>{t('teslaDesc', { site: 'roardrive.com' })}</span></span>
                  {inApp && (screensLive >= 2 ? (
                    // THE hand-off Marc remembers: the car screen is listening on the relay, so the phone
                    // can throw the drive straight onto it. This lived only under DRIVE, which is why he
                    // went looking for it here and found a useless 'open browser' button instead.
                    sentToScreen
                      ? <span class="inp__desc inp__desc--fine">{t('sentToScreen')}</span>
                      : <button class="pill pill--btn inp__go" onClick={(e) => { e.stopPropagation(); try { playWs.current?.send(JSON.stringify({ type: 'control', car: profile.id })); setSentToScreen(true); setTimeout(() => setSentToScreen(false), 5000); } catch { /* relay hiccup */ } }}>{t('playOnScreen')}</button>
                  ) : (
                    // No car screen listening yet. One line is enough: the long instructions belong
                    // nowhere near a card this size.
                    <span class="inp__desc inp__desc--fine">{t('teslaFine')}</span>
                  ))}
                  {/* always on show (Marc): the Commander is the reason this card exists, so its hairline
                      and its row are permanent — only the steps inside fold away */}
                  {/* the panel only swallows taps once the card is ON. Before that, tapping anywhere in it
                      selects the card: Marc tapped the Commander row itself, the panel ate the tap, the
                      card stayed unselected and the bridge never started. */}
                  {(
                    <div class="hwpanel" onClick={(e) => { if (on('tesla') || inCarScreen) e.stopPropagation(); }}>
                      {/* the Commander belongs HERE (Marc): it adds no refresh rate at all — it adds the
                          things the car alone knows. Pedal and brake are NOT its selling point any more:
                          doors, lights and wipers are, plus launch armed from the two pedals. */}
                      {/* opening the row is the intent (Marc): it selects the Tesla card, which is what powers
                          the phone's bridge up in Commander mode, and it nudges the bridge directly too.
                          The state word is quiet grey until something is actually flowing. */}
                      <details class="hwrow" onToggle={(e) => {
                        const open = (e.currentTarget as HTMLDetailsElement).open;
                        setCmOpen(open);
                        if (!open) return;
                        if (!on('tesla') && !inCarScreen) toggle('tesla');
                        if (inApp) { try { (window as Window & { RoarBridge?: { postMessage: (m: string) => void } }).RoarBridge?.postMessage('on:cm'); } catch { /* older app */ } }
                      }}>
                        <summary><i class={relay.canOk ? 'hwrow__ok' : relay.cmAnswering ? 'hwrow__mid' : ''} /><b>{t('commander')}</b><em class={relay.canOk || relay.cmAnswering ? '' : 'hwrow__hint'}>{relay.canOk ? '✓ CAN' : relay.cmAnswering ? t('answering') : !cmOpen ? t(soonOnWeb ? 'experimentalSoon' : 'experimental') : !inApp ? t('waitingApp') : relay.pdSt === 'rx' ? t('linkedWaiting') : t('connecting')}</em></summary>
                        <p class="hwrow__lead">{t('commanderLead', { n: 'N', d: 'D', doors: t('doors'), lights: t('lights'), wipers: t('wipers'), launch: t('launchControl') })}</p>
                        <ol class="hwsteps">
                          <li>{t('cmStep1', { scan: 'ScanMyTesla ON' })}</li>
                          <li>{t('cmStep2', { ssid: 'S3XY_OBD', pass: '12345678' })}</li>
                          <li>{t('cmStep3', { local: 'Local Network' })}</li>
                          {inApp && relay.pdSt === 'knock' && bridgeCmd !== 'off' && (
                            <li class="hwsteps__note">
                              {/* knocking with no reply has exactly three causes, and iOS's Local Network
                                  permission is the silent one: denied once, it blocks the packets forever
                                  and looks identical to nobody being there */}
                              <b>{t('cmNoReply')}</b> {t('cmNoReplyBody')}
                              {relay.pdErr && <><br /><code>{relay.pdErr}</code></>}
                            </li>
                          )}
                          {/* the wiper hunter moved here with the Commander — it is the only row it belongs
                              to now, and only someone whose Commander is answering can use it anyway */}
                          {inApp && (relay.canOk || relay.cmAnswering) && (
                            <li class="hwsteps__note">
                              <b>{t('findWiper')}</b> {t('findWiperBody', { off: '', on: '' })}
                              {' '}<button class="hwrow__link" onClick={() => huntMark('a')}>{t('off')}</button>
                              {' · '}<button class="hwrow__link" onClick={() => huntMark('b')}>{t('on')}</button>
                              {' · '}<button class="hwrow__link" onClick={() => huntMark('clear')}>{t('reset')}</button>
                              {huntMsg && <><br /><code>{huntMsg}</code></>}
                            </li>
                          )}
                        </ol>
                      </details>
                    </div>
                  )}
                </div>
              )}
              {/* not a choice: whatever else is selected, this is what the engine falls back to. It stays on
                  screen even from the car's own browser — Marc lost sight of it there and asked for it back. */}
              <div class={`inp inp--src inp--floor ${simOn ? 'inp--muted' : ''} ${soonOnWeb ? 'inp--soon inp--comingsoon' : ''} ${!soonOnWeb && !simOn && !inCarScreen && !relay.raceboxOk && !relay.obdOk ? 'inp--detected inp--detected--mid' : ''}`}>
                {/* once something better is on, this card says what it IS from then on: the backup. 'Delay'
                    next to a source you are not even using read as a warning (Marc) */}
                <span class="inp__title">{t('phoneGps')}<em class="inp__grade inp__grade--mid">{(picks.length && !simOn) || inCarScreen ? t('backup') : t('delay')}</em></span>
                {/* the real figure whenever this phone's GPS can actually be timed, and the honest default
                    otherwise — a phone is about 1 Hz. Never an instruction: this is a card, not a button. */}
                <span class="inp__meta">{t('phoneMeta', { hz: gpsMeasured && !inCarScreen ? gpsLabel : '1 Hz' })}</span>
                <span class="inp__desc"><span>{(picks.length && !simOn) || inCarScreen ? t('phoneDescBackup') : t('phoneDescAlone')}</span></span>
                {soonOnWeb && <em class="inp__soon inp__soon--foot">{t('comingSoon')}</em>}
              </div>
              {showGps && <p class="hwpanel__foot">gps rate: {gpsHz}</p>}
              {/* the simulator is FREE for everyone (Marc): the engine, driven with two fingers, no car
                  and no GPS. The instrumented one (log, audio levels, telemetry pills) stays admin. */}
              {/* the simulator is the APP's (Marc): the web sells the engine and drives the Tesla's own
                  screen. Marc's instrumented SIM stays wherever he signs in. */}
              {inApp && (
                <div class={`inp inp--src inp--host inp--sim ${on('sim') && !simAdmin ? 'inp--on' : ''}`} role="button" tabIndex={0} onClick={() => pickSim(false)}>
                  <span class="inp__title">{t('sim')}<em class="inp__grade inp__grade--free">{t('simFree')}</em></span>
                  <span class="inp__meta">{t('simMeta')}</span>
                  <span class="inp__desc"><span>{t('simDesc')}</span></span>
                </div>
              )}
              {admin && (
                <div class={`inp inp--src inp--host inp--sim ${on('sim') && simAdmin ? 'inp--on' : ''}`} role="button" tabIndex={0} onClick={() => pickSim(true)}>
                  <span class="inp__title">{t('simAdminName')}<em>admin</em></span>
                  <span class="inp__desc"><span>{t('simAdminDesc')}</span></span>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <div class="bay__section">
        <div class="eyebrow bay__eyebrow">{t('gearRatio')}</div>
        <p class="bay__why">{t('gearRatioWhy')}</p>
        <div class="setup">
          <section class="setup__block">
            <span class="seg seg--wide" role="group" aria-label="Gear ratio">
              <button class={`seg__opt ${gearing === 'porsche' ? 'seg__opt--on' : ''}`} onClick={() => pickGearing('porsche')}>{t('realistic')}</button>
              <button class={`seg__opt ${gearing === 'own' ? 'seg__opt--on' : ''}`} onClick={() => pickGearing('own')}>{t('yourCarTab')}</button>
            </span>
            {gearing === 'porsche' && (
              <div class="realistic">
                <p class="setup__note">{t('realisticNote')}</p>
                {/* THIS car's gearbox, not the catalogue. It used to list every public engine, which read as
                    'this is your car' back when the Porsche was the only one: on any other pit box it
                    showed the Porsche's ladder next to a Lamborghini (Marc's screenshot). One box, one
                    context. */}
                <ul class="realistic__list">
                  {[profile].map((c) => (
                    <li key={c.id}>
                      {/* the real ladder as a table, one cell a gear: number, the speed it tops out at, and a
                          bar for how far up the range that is. The unit is said once. */}
                      <span class="realistic__car"><small>{c.manufacturer}</small><b>{c.name}</b><span class="realistic__unit">{t('topSpeedPerGear', { unit: units === 'mph' ? 'mph' : 'km/h' })}</span></span>
                      <ol class="ladder" style={{ '--n': c.gearTopSpeedsKmh.length } as Record<string, number>}>
                        {c.gearTopSpeedsKmh.map((v, i) => (
                          <li key={i} style={{ '--w': v / c.gearTopSpeedsKmh[c.gearTopSpeedsKmh.length - 1] } as Record<string, number>}>
                            <em>{i + 1}</em><b>{gearSpeed(v)}</b><i />
                          </li>
                        ))}
                      </ol>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {gearing === 'own' && (<>
            <div class="setup__head setup__head--seg setup__head--sub">
              <span class="seg seg--wide" role="group" aria-label="Car type">
                <button class={`seg__opt ${kind === 'ev' ? 'seg__opt--on' : ''}`} onClick={() => pickKind('ev')}>{t('ev')}</button>
                <button class={`seg__opt ${kind === 'ice' ? 'seg__opt--on' : ''}`} onClick={() => pickKind('ice')}>{t('ice')}</button>
              </span>
            </div>
            {/* ONE table for every gearbox (Marc: 'parecida a la de realistic para que puedan editar fácilmente'):
                the EV shows the ladder its top speed produces; an ICE car shows its own ladder with every speed
                editable in its cell. Touching a cell turns the car into 'your gears', with a way back. */}
            {kind === 'ev' ? (
              <div class="owncar">
                <div class="setup__row">
                  <label class="setup__label" for="topspeed">{t('topSpeed')}</label>
                  <select id="topspeed" class="vehicle__select setup__select" value={String(topClamped)} onChange={(e) => pickTop(Number((e.currentTarget as HTMLSelectElement).value))}>
                    {topOptions.map((kmh) => (
                      <option key={kmh} value={String(kmh)}>{topLabel(kmh)}</option>
                    ))}
                  </select>
                  <span class="seg seg--small" role="group" aria-label="Speed units">
                    <button class={`seg__opt ${units === 'kmh' ? 'seg__opt--on' : ''}`} onClick={() => pickUnits('kmh')}>km/h</button>
                    <button class={`seg__opt ${units === 'mph' ? 'seg__opt--on' : ''}`} onClick={() => pickUnits('mph')}>mph</button>
                  </span>
                </div>
                <div class="gearcard">
                  <span class="realistic__car"><small>{t('ev')}</small><b>{t('topSpeedOf', { speed: showSpeed(topClamped) })}</b><span class="realistic__unit">{t('evLadderNote', { unit: units === 'mph' ? 'mph' : 'km/h' })}</span></span>
                  {ladderTable(evLadder, false)}
                </div>
              </div>
            ) : (
              <div class="owncar">
                {/* the measured gearbox comes FIRST: a catalogue entry is a guess about a car like
                    yours, this is your car (Marc: 'aprendizaje por OBD') */}
                <div class={`learn ${learn?.ready ? 'learn--ready' : ''}`}>
                  <div class="learn__head">
                    <b>{learnSim ? t('learnTitleSim') : t('learnTitle')}</b>
                    {learn && learn.samples > 0 && <em>{t('learnProgress', { gears: learn.gears.length, min: Math.max(1, Math.round(learn.seconds / 60)) })}</em>}
                  </div>
                  {!learn || learn.samples === 0 ? (
                    <>
                      <p class="learn__hint">{learnSim ? t('learnHintSim') : t('learnHint')}</p>
                      {!learnSim && <p class="learn__hint learn__hint--fine">{t('learnNotRacebox')}</p>}
                    </>
                  ) : (
                    <>
                      <ol class="learn__gears">
                        {learn.gears.map((g, i) => (
                          <li key={i}><em>{i + 1}</em><b>{gearSpeed(g.topKmh)}</b></li>
                        ))}
                      </ol>
                      {following > 0 && <p class="learn__live">{t('learnFollowing', { gear: following })}</p>}
                      <p class="learn__hint">
                        {learn.ready
                          ? t('learnReady', { rpm: learn.maxRpm })
                          : t('learnKeepGoing')}
                      </p>
                      {learn.ready && (
                        <button class="pill pill--btn" onClick={() => {
                          const tops = learnedLadder();
                          setCustomGears(tops.map((x) => String(gearSpeed(x))));
                          setEdited(true);
                          applyGears(tops, 'custom');
                        }}>{t('learnUse')}</button>
                      )}
                      {learn.ready && <p class="learn__hint learn__hint--fine">{t('learnUseWhy')}</p>}
                      {/* the one switch there is. ON by default: copying the driver's own box beats any
                          ladder, and it can only act when it recognises the gear (Marc asked how manual
                          and auto sit with this — like this: picking one by hand turns the copying off) */}
                      <div class="learn__follow">
                        <span>{t('learnFollowLabel')}</span>
                        <span class="seg seg--small" role="group" aria-label="Follow my gears">
                          <button class={`seg__opt ${followOn ? 'seg__opt--on' : ''}`} onClick={() => { setFollowOn(true); currentSession()?.setFollowMyGears(true); }}>{t('yes')}</button>
                          <button class={`seg__opt ${!followOn ? 'seg__opt--on' : ''}`} onClick={() => { setFollowOn(false); currentSession()?.setFollowMyGears(false); }}>{t('no')}</button>
                        </span>
                      </div>
                      <button class="hwrow__link learn__reset" onClick={() => { currentSession()?.resetGearLearning(); setLearn(currentSession()?.gearLearning(learnSim) ?? null); }}>{t('learnForget')}</button>
                    </>
                  )}
                </div>
                <div class="icecar__row">
                  <select class="vehicle__select" value={iceBrand} onChange={(e) => { const brand = (e.currentTarget as HTMLSelectElement).value; setIceBrand(brand); setEdited(false); if (brand === 'custom') pickIceCar('custom'); else { const first = ICE_CARS.find((c) => c.brand === brand); if (first) pickIceCar(first.id); } }}>
                    <option value="">{t('brandPick')}</option>
                    {ICE_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                    <option value="custom">{t('myOwnGears')}</option>
                  </select>
                  {iceBrand && iceBrand !== 'custom' ? (
                    <select class="vehicle__select" value={iceCar} onChange={(e) => { setEdited(false); pickIceCar((e.currentTarget as HTMLSelectElement).value); }}>
                      {ICE_CARS.filter((c) => c.brand === iceBrand).map((c) => <option key={c.id} value={c.id}>{c.model}</option>)}
                    </select>
                  ) : <span />}
                </div>
                {shownGears && (
                  <div class="gearcard">
                    <span class="realistic__car">
                      <small>{isCustom ? t('yourGears') : ICE_CARS.find((c) => c.id === iceCar)?.brand}</small>
                      <b>{isCustom ? (edited && baseCar ? t('editedSuffix', { model: baseCar.model }) : t('myOwnGears')) : baseCar?.model}</b>
                      <span class="realistic__unit">{t('speedAtLimiter', { unit: units === 'mph' ? 'mph' : 'km/h' })}</span>
                    </span>
                    {ladderTable(shownGears, true)}
                    <div class="gearcard__foot">
                      <span class="seg seg--small" role="group" aria-label="Speed units">
                        <button class={`seg__opt ${units === 'kmh' ? 'seg__opt--on' : ''}`} onClick={() => pickUnits('kmh')}>km/h</button>
                        <button class={`seg__opt ${units === 'mph' ? 'seg__opt--on' : ''}`} onClick={() => pickUnits('mph')}>mph</button>
                      </span>
                      {isCustom && shownGears.length < 8 && <button class="pill pill--btn" onClick={() => { const next = [...customGears, String(Math.round(Number(customGears[customGears.length - 1] || 200) + 30))]; setCustomGears(next); const clean = cleanGearTops(next.map((x) => (units === 'mph' ? Number(x) * KMH_PER_MPH : Number(x)))); if (clean) applyGears(clean, 'custom'); }}>{t('addGear')}</button>}
                      {isCustom && shownGears.length > 3 && <button class="pill pill--btn" onClick={() => { const n = customGears.slice(0, -1); setCustomGears(n); const clean = cleanGearTops(n.map((x) => (units === 'mph' ? Number(x) * KMH_PER_MPH : Number(x)))); if (clean) applyGears(clean, 'custom'); }}>{t('removeGear')}</button>}
                      {edited && baseCar && <button class="pill pill--btn" onClick={() => { setEdited(false); pickIceCar(baseCar.id); }}>{t('backToCar', { model: baseCar.model })}</button>}
                      {isCustom && gearTopsProblem(customGears.map((x) => (units === 'mph' ? Number(x) * KMH_PER_MPH : Number(x)))) && <span class="icecar__err">{gearTopsProblem(customGears.map((x) => (units === 'mph' ? Number(x) * KMH_PER_MPH : Number(x))))}</span>}
                      <span class="icecar__limits">{t('tapToChange', { limits: GEAR_LIMITS_KMH.slice(0, 6).map((l) => l[1]).join(' · ') })}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            </>)}
          </section>
        </div>
      </div>

      {/* WITHOUT COVERAGE: a verdict in words carrying the colour, the megabytes quiet in mono under it
          (Marc: words carry the palette, the numbers go below without shouting). Grey while it works,
          green when the car is on the device, amber when it needs the driver: a download that did not
          finish, no network right now. It downloads by itself; the only button is the retry. A demo car
          has no row: its audio never lands on disk, by design. */}
      {unlocked && offlineSupported() && off.state !== 'unavailable' && (
      <div class="bay__section">
        <div class="eyebrow bay__eyebrow">{t('offline')}</div>
        <p class="bay__why">{t('offlineWhy')}</p>
        <div class={`offline offline--${off.state}`}>
          <span class="offline__dot" />
          <div class="offline__text">
            <b class="offline__verdict">{offlineVerdict(off)}</b>
            {offlineMeta(off) && <span class="offline__meta">{offlineMeta(off)}</span>}
          </div>
          {off.state === 'failed' && <button class="pill pill--btn" onClick={() => void ensureOffline(profile, true)}>{t('offlineRetry')}</button>}
        </div>
        {off.state === 'downloading' && <div class="offline__bar"><i style={{ width: `${Math.min(100, Math.round((off.doneBytes / Math.max(1, off.totalBytes)) * 100))}%` }} /></div>}
      </div>
      )}
      </div>
      </div>

      {showAccount && <AccountModal onClose={() => { setShowAccount(false); setEmail(getAccountEmail()); }} />}
    </main>
  );
}
